<template>
  <div class="sync-tasks-view">
    <section class="p-3">
      <template>
        <div>
          <account-header :title="$t('imports.canvasSyncTasks')" />
          <div class="table-container">
            <eg-table-base
              class="essay-table w-100"
              :selectable="false"
              split-select
              :items="tasks"
              :fields="fields"
              :total="tasks.length"
              :limit="tasks.length + 1"
              :loading="loading"
              :initial-loading="loading"
              data-testid="eg-selectable-table"
              @onChangePage="$emit('onChangePage', $event)"
            >
              <template #empty-state>
                <empty-state
                  v-if="!loading && !tasks.length"
                  key="empty"
                  :title="$t('imports.noCanvasSyncTasks')"
                  description="Add a new Canvas task to sync it with EssayGrader."
                  button-text="Add new task"
                  button-path="/imports/canvas"
                  class="m-auto"
                  data-testid="empty-state"
                />
              </template>

              <template #head(actions)="{ label }">
                {{ label }}
              </template>
              <template #table-colgroup="{ fields }">
                <col
                  v-for="field in fields"
                  :key="field.key"
                  :class="field.columnClass"
                  data-testid="table-colgroup"
                />
              </template>

              <template #cell(status)="{ item }">
                <cell-status
                  :item="item"
                  :is-grading="false"
                  data-testid="cell-status"
                />
              </template>

              <template #cell(Rubric)="{ item }">
                <span v-if="item.Rubric" class="small">
                  {{ item.Rubric.name || `#${item.RubricId}` }}
                </span>
                <span v-else-if="item.type" class="small">
                  {{ formatDefaultType(item) }}
                </span>
                <span v-else class="text-gray small">None</span>
              </template>
              <template #cell(Assignment)="{ item }">
                <span v-if="item.Assignment" class="small">
                  {{ item.Assignment.name || `#${item.assignmentId}` }}
                </span>
                <span v-else class="text-gray small">None</span>
              </template>
              <template #cell(Class)="{ item }">
                <span v-if="item.Class" class="small">
                  {{ `#${item.Class.extId} ${item.Class.name}` }}
                </span>
                <span v-else class="text-gray small">None</span>
              </template>
              <template #cell-actions="{ item }">
                <eg-dropdown
                  ref="dropdown"
                  class="sync-tasks-actions-dropdown"
                  variant="outline-secondary"
                  no-caret
                  boundary="window"
                  right
                >
                  <template #button-content>
                    <eg-b-icon
                      icon="three-dots-vertical"
                      aria-hidden="true"
                    ></eg-b-icon>
                  </template>

                  <eg-b-dropdown-item @click="toggleTask(item)">
                    {{ item.status === "running" ? "Stop" : "Start" }}
                  </eg-b-dropdown-item>

                  <eg-b-dropdown-divider></eg-b-dropdown-divider>

                  <eg-b-dropdown-item variant="danger" @click="onDelete(item)">
                    Delete
                  </eg-b-dropdown-item>
                </eg-dropdown>
              </template>
            </eg-table-base>
          </div>
        </div>
      </template>
    </section>
  </div>
</template>

<script>
import { getSyncTask, deleteSyncTask, updateSyncTask } from "@/api";
import { formatDefaultType } from "@/utils/format";
import formatDate from "@/utils/formatDate";
import CellStatus from "@/components/essay/table/cell_status.vue";
import { EgTableBase } from "@/components/global/eg/eg_table";
import EmptyState from "@/components/global/empty_state.vue";
import AccountHeader from "@/views/account/account_header.vue";

const DEFAULT_FIELDS = [
  {
    key: "status",
    label: "Status",
    columnClass: "col-status",
    sortable: false,
  },
  {
    key: "Class",
    label: "Class",
    columnClass: "col-class",
    sortable: false,
  },
  {
    key: "Assignment",
    label: "Assignment",
    columnClass: "col-assignment",
    sortable: false,
  },
  {
    key: "Rubric",
    label: "Rubric",
    columnClass: "col-rubric",
    sortable: false,
  },
  {
    key: "createdAt",
    label: "Created At",
    columnClass: "col-created-at",
    sortable: false,
    formatter: (value) => {
      return formatDate(value);
    },
  },
  {
    key: "updatedAt",
    label: "Updated At",
    columnClass: "col-updated-at",
    sortable: false,
    formatter: (value) => {
      return formatDate(value);
    },
  },
  {
    key: "actions",
    label: "",
    tdClass: "text-right",
    columnClass: "col-actions",
  },
];
export default {
  name: "CanvasSyncTasksView",

  components: {
    AccountHeader,
    EgTableBase,
    CellStatus,
    EmptyState,
  },

  data() {
    return {
      loading: true,
      tasks: [],
      fields: DEFAULT_FIELDS,
    };
  },

  created() {
    this.fetchTasks();
  },

  methods: {
    formatDefaultType,

    async fetchTasks() {
      this.loading = true;
      const response = await getSyncTask();
      this.tasks = response.data;
      this.loading = false;
    },

    async onDelete(item) {
      this.loading = true;
      await deleteSyncTask(item.id);
      this.tasks = this.tasks.filter((task) => task.id !== item.id);
      this.loading = false;
    },

    async toggleTask(item) {
      this.loading = true;
      const status = item.status === "running" ? "stopped" : "running";
      await updateSyncTask(item.id, {
        status,
      });

      this.tasks = this.tasks.map((task) => {
        if (task.id === item.id) {
          task.status = status;
        }
        return task;
      });

      this.loading = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.sync-tasks-view {
  ::v-deep .b-table-sticky-header {
    max-height: calc(100vh - 151px);
  }

  .table-container {
    border-radius: var(--radius-rd-400, 12px);
    border: 1px solid var(--color-stroke-neutral-default);
    overflow: auto;
  }
}
</style>
